import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createBoostPackage, updateBoostPackage, getBoostPackages } from '../../services/api';
import { 
  FiArrowLeft,
  FiSave,
  FiX,
  FiPlus,
  FiCheck,
  FiStar,
  FiPackage,
  FiCalendar,
  FiDollarSign,
  FiList,
  FiAlertCircle,
  FiSettings
} from 'react-icons/fi';

function BoostPackageForm({ admin }) {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(packageId);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_days: 7,
    price: 0,
    original_price: '',
    max_listings: '',
    features: [],
    is_popular: false,
    is_active: true
  });
  const [currentFeature, setCurrentFeature] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchPackage();
    }
  }, [packageId]);

  const fetchPackage = async () => {
    try {
      const response = await getBoostPackages();
      if (response.success) {
        const pkg = response.data.packages.find(p => p.package_id == packageId);
        if (pkg) {
          setFormData({
            ...pkg,
            features: pkg.features ? JSON.parse(pkg.features) : [],
            max_listings: pkg.max_listings || ''
          });
          setSuccessMessage('Package loaded successfully');
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError('Package not found');
        }
      } else {
        setError(response.message || 'Failed to load package');
      }
    } catch (err) {
      setError('Failed to load package details: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        original_price: formData.original_price || null,
        max_listings: formData.max_listings || null
      };

      const response = isEdit 
        ? await updateBoostPackage(packageId, submitData)
        : await createBoostPackage(submitData);

      if (response.success) {
        setSuccessMessage(`Package ${isEdit ? 'updated' : 'created'} successfully!`);
        setTimeout(() => {
          navigate('/admin/boost/packages');
        }, 1500);
      } else {
        throw new Error(response.message || `Failed to ${isEdit ? 'update' : 'create'} package`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = () => {
    if (currentFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, currentFeature.trim()]
      });
      setCurrentFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">
                {isEdit ? 'Edit' : 'Create'} 
              </span> Boost Package
            </h1>
            <p className="text-gray-400 mt-1">
              {isEdit ? 'Update package details and pricing' : 'Create a new listing boost package'}
            </p>
          </div>
          <Link
            to="/admin/boost/packages"
            className="text-gray-400 hover:text-white text-sm font-medium flex items-center transition-colors hover:scale-[1.02]"
          >
            <FiArrowLeft className="mr-2" />
            Back to Packages
          </Link>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheck className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="p-6 md:p-8 space-y-8">
              {/* Basic Information Card */}
              <div className="bg-gray-750 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center">
                    <FiPackage className="text-blue-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Basic Information</h3>
                    <p className="text-sm text-gray-400">Package name and duration settings</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Package Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Package Name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="e.g., Premium Boost"
                        required
                      />
                      <FiPackage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    </div>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Duration (Days) *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                        min="1"
                        max="365"
                        value={formData.duration_days}
                        onChange={(e) => handleChange('duration_days', parseInt(e.target.value) || 7)}
                        required
                      />
                      <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      How long the boost effect lasts
                    </p>
                  </div>

                  {/* Max Listings */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Maximum Listings
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                        min="1"
                        value={formData.max_listings}
                        onChange={(e) => handleChange('max_listings', e.target.value || '')}
                        placeholder="Leave empty for unlimited"
                      />
                      <FiList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Maximum listings that can be boosted with this package
                    </p>
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe the benefits of this package..."
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Card */}
              <div className="bg-gray-750 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center">
                    <FiDollarSign className="text-green-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Pricing</h3>
                    <p className="text-sm text-gray-400">Set package pricing and discounts</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Current Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Price (₦) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">₦</span>
                      <input
                        type="number"
                        step="1"
                        className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                        min="0"
                        value={formData.price}
                        onChange={(e) => handleChange('price', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </div>
                  </div>

                  {/* Original Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Original Price (₦)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">₦</span>
                      <input
                        type="number"
                        step="1"
                        className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                        min="0"
                        value={formData.original_price}
                        onChange={(e) => handleChange('original_price', e.target.value || '')}
                        placeholder="For discounted pricing"
                      />
                    </div>
                    {formData.original_price && formData.original_price > formData.price && (
                      <div className="mt-2 px-3 py-1 bg-green-900/20 text-green-400 text-sm rounded-lg border border-green-800/50">
                        Discount: {Math.round((1 - formData.price / formData.original_price) * 100)}% off
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Features Card */}
              <div className="bg-gray-750 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-900/30 flex items-center justify-center">
                    <FiList className="text-purple-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Features</h3>
                    <p className="text-sm text-gray-400">Add benefits and features of this package</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Add Feature Input */}
                  <div className="flex space-x-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                        placeholder="Add a feature (e.g., Priority listing placement)"
                        value={currentFeature}
                        onChange={(e) => setCurrentFeature(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                      />
                      <FiPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    </div>
                    <button
                      type="button"
                      className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleAddFeature}
                      disabled={!currentFeature.trim()}
                    >
                      <FiPlus className="mr-2" />
                      Add
                    </button>
                  </div>
                  
                  {/* Features List */}
                  <div className="border border-gray-700 rounded-lg p-4 bg-gray-700/30 min-h-[120px]">
                    {formData.features.length === 0 ? (
                      <div className="text-center text-gray-500 py-8 flex flex-col items-center">
                        <FiList className="text-4xl mb-2 opacity-30" />
                        <p>No features added yet</p>
                        <p className="text-sm mt-1">Add features to make your package attractive</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {formData.features.map((feature, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg border border-gray-600 group hover:border-gray-500 transition-colors">
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 rounded-full bg-[#ff660e]/20 flex items-center justify-center">
                                <FiCheck className="text-[#ff660e] text-sm" />
                              </div>
                              <span className="text-gray-300">{feature}</span>
                            </div>
                            <button
                              type="button"
                              className="text-gray-400 hover:text-red-400 transition-colors p-1"
                              onClick={() => handleRemoveFeature(index)}
                              title="Remove feature"
                            >
                              <FiX />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Settings Card */}
              <div className="bg-gray-750 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-yellow-900/30 flex items-center justify-center">
                    <FiSettings className="text-yellow-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Package Settings</h3>
                    <p className="text-sm text-gray-400">Configure package visibility and flags</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Popular Package */}
                  <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
                        <FiStar className="text-[#ff660e]" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Mark as Popular Package</p>
                        <p className="text-sm text-gray-400">Highlight this package as a popular choice</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.is_popular}
                        onChange={(e) => handleChange('is_popular', e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ff660e]"></div>
                    </label>
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center">
                        <FiCheck className="text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Package is Active</p>
                        <p className="text-sm text-gray-400">Make this package available for purchase</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.is_active}
                        onChange={(e) => handleChange('is_active', e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="bg-gray-900 px-6 py-4 border-t border-gray-700">
              <div className="flex flex-col md:flex-row justify-end gap-3">
                <Link
                  to="/admin/boost/packages"
                  className="px-6 py-2.5 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-all duration-200 font-medium text-center hover:scale-[1.02]"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center justify-center hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="mr-2" />
                      {isEdit ? 'Update Package' : 'Create Package'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default BoostPackageForm;